// import DataTable from "react-data-table-component"
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ZoomInIcon,
  ChartIcon,
  StarIcon,
  SearchIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@iconicicons/react';
import { CornerDownRight } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
// import { UncontrolledTooltip } from 'reactstrap';
import { numberFormatter } from '../helpers/formatters';

const FLOW_DIRECTION_SHORT: Record<string, string> = {
  RECEIPT: 'REC',
  DELIVERY: 'DEL',
  FORWARDHAUL: 'FH',
  BACKHAUL: 'BH',
};

const toDirectionShort = (direction?: string | null) =>
  direction ? FLOW_DIRECTION_SHORT[direction] ?? direction : '';
import { ReusableModal } from '../modals/ReusableModal';
import { ComponentLocationManager } from '../capacityComponents/ComponentLocationManager';
import { CapacityComponentHistory } from './CapacityComponentHistory';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import {
  useCapacityComponentsFlows,
  useClientPagination,
  useToggleFavorite,
} from '../helpers/hooks';
import { selectAllNodes, selectTspOptions } from '../map/slice/mapSelectors';
import { plotterActions } from '../plotter/plotterSlice';
import { useFocusPoint } from '../hooks/useFocusPoint';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { useGetTSPsQuery } from '../services/edgeApi';
import { CapacityComponent, CapacityType, Flow, FlowSliceKey } from '@/types';
import { CAPACITY_TYPE_OPTIONS } from '../capacityComponents/capacityTypeOptions';

type FlowRow = {
  id: Flow['id'];
  compositeId: Flow['composite_id'];
  key: FlowSliceKey | undefined;
  providerName?: string;
  isUnmatchedProviderLink: boolean;
  isFavorite: boolean;
  pipeline: Flow['tsp_name'];
  description: Flow['description'];
  noticePriority: Flow['best_available_notice']['priority'];
  noticeOAC: Flow['best_available_notice']['amount'];
  noticePercent: Flow['best_available_notice']['percent'];
  seg: Flow['segment'];
  ourDirection: Flow['best_available']['flow_direction'];
  gasDate: Flow['best_available']['gas_date'];
  cycle: Flow['best_available']['nomination_cycle'];
  scheduled: Flow['best_available']['scheduled_volume'];
  utilization: Flow['best_available']['utilization'];
  oac: Flow['best_available']['oac'];
  opcap: Flow['best_available']['opcap'];
  type: Flow['capacity_type'];
  itFlow: Flow['best_available']['interruptible_flow'];
  locations: number;
  notice: Flow['best_available_notice'];
  nodes: Flow['nodes'];
  points: Flow['points'];
};

type DisplayFlowRow = FlowRow & {
  rowKey: string;
  groupKey: string;
  isRollupParent: boolean;
  isRawChild: boolean;
  rawChildCount: number;
};

export type CapacityComponentFlowsTableProps = {
  nomCycle: string;
  gasDate: string;
};

// Toggle to switch between dispatching parent rows only vs all visible rows to map
// TODO: this is intended to let us try showing just net, or also showing whatever expanded raw flows are expanded in the table. I don't think it's working correctly yet to display the expanded rows, probably because of deduplication of some sort.  I'm also not sure that it's useful to show the raw flows on the map, but leaving this here so we can try it both ways if we want.
const PUSH_VISIBLE_ROWS = true;

export const CapacityComponentFlowsTable: React.FC<
  CapacityComponentFlowsTableProps
> = ({ nomCycle, gasDate }) => {
  useGetTSPsQuery();
  const focusPoint = useFocusPoint();
  const dispatch = useDispatch();
  const allNodes = useSelector(selectAllNodes);
  const [sortKey, setSortKey] = useState<keyof FlowRow>('isFavorite');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // const [flowHistoryOpen, setFlowHistoryOpen] = useState<boolean>(false);
  const [flowSliceKey, setFlowSliceKey] = useState<FlowSliceKey | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const [componentHistoryDescription, setComponentHistoryDescription] =
    useState<string | false>(false);
  const [componentId, setComponentId] = useState<number | false>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [capacityTypeFilter, setCapacityTypeFilter] = useState<
    CapacityType | undefined
  >(undefined);
  const [component, setComponent] = useState<CapacityComponent | null>(null);
  const [allMappedSegments, setAllMappedSegments] = useState<boolean>(false);
  const [filterTsps, setFilterTsps] = useState<number[]>([]);
  const [tableScrollRoot, setTableScrollRoot] = useState<HTMLElement | null>(
    null,
  );
  const [tableScrollHeight, setTableScrollHeight] = useState<number | null>(
    null,
  );
  const tspOptions = useSelector(selectTspOptions);

  useEffect(() => {
    if (!tableScrollRoot) {
      return;
    }

    const containerEl = tableScrollRoot.closest(
      '#below-map-context',
    ) as HTMLElement | null;

    let rafId: number | null = null;

    const updateScrollHeight = () => {
      if (!tableScrollRoot.isConnected) {
        return;
      }

      // Reactstrap Collapse toggles between `show` and `collapsing` while closing.
      // Skip updates unless fully open to avoid stepwise shrink during collapse.
      if (containerEl && !containerEl.classList.contains('show')) {
        return;
      }

      const { top } = tableScrollRoot.getBoundingClientRect();
      const boundaryBottom =
        containerEl?.getBoundingClientRect().bottom ?? window.innerHeight;
      const verticalPadding = 8;
      const nextHeight = Math.max(
        220,
        Math.floor(boundaryBottom - top - verticalPadding),
      );

      setTableScrollHeight((prevHeight) =>
        prevHeight !== null && Math.abs(prevHeight - nextHeight) < 2
          ? prevHeight
          : nextHeight,
      );
    };

    const queueScrollHeightUpdate = () => {
      if (rafId !== null) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateScrollHeight();
      });
    };

    queueScrollHeightUpdate();

    window.addEventListener('resize', queueScrollHeightUpdate);
    window.addEventListener('below-map-resize', queueScrollHeightUpdate);

    return () => {
      window.removeEventListener('resize', queueScrollHeightUpdate);
      window.removeEventListener('below-map-resize', queueScrollHeightUpdate);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [tableScrollRoot]);

  const { flows, flowsLoading, flowsFetching, fetchNextPage, reset } =
    useCapacityComponentsFlows(
      {
        gasDate,
        nomCycle,
        itemsPerPage: 30,
        capacityType: capacityTypeFilter,
        searchText,
        tsps: filterTsps,
      },
      allMappedSegments,
    );

  useEffect(() => {
    reset();
  }, [gasDate, nomCycle]);

  const initialFavorites = useMemo(() => {
    if (!flows) {
      return {};
    }
    return Object.values(flows).reduce(
      (acc, curr) => ({ ...acc, [curr.id]: curr.is_favorite }),
      {},
    );
  }, [flows]);

  const {
    getFavorite,
    toggleFavorite,
    isLoading: toggleFavoriteLoading,
  } = useToggleFavorite('capacitycomponent', initialFavorites);

  const data = useMemo<FlowRow[]>(() => {
    if (!flows) {
      return [];
    }

    return Object.values(flows).map((flow) => ({
      id: flow.id,
      compositeId: flow.composite_id,
      key: flow.key,
      providerName: flow.provider_name,
      isUnmatchedProviderLink: Boolean(flow.is_unmatched_provider_link),
      isFavorite: getFavorite(flow.id),
      pipeline: flow.tsp_name,
      description: flow.description,
      noticePriority: flow.best_available_notice?.priority,
      noticeOAC: flow.best_available_notice?.amount,
      noticePercent: flow.best_available_notice?.percent,
      seg: flow.segment,
      ourDirection: flow.best_available?.flow_direction,
      gasDate: flow.best_available?.gas_date,
      cycle: flow.best_available?.nomination_cycle,
      scheduled: flow.best_available?.scheduled_volume,
      utilization: flow.best_available?.utilization,
      oac: flow.best_available?.oac,
      opcap: flow.best_available?.opcap,
      type: flow.capacity_type,
      itFlow: flow.best_available?.interruptible_flow,
      locations: flow.points.length,
      notice: flow.best_available_notice,
      nodes: flow.nodes,
      points: flow.points,
    }));
  }, [flows, getFavorite]);

  const compareRows = useCallback(
    (a: FlowRow, b: FlowRow) => {
      let sortValue = 0;
      if (typeof a[sortKey] !== typeof b[sortKey]) sortValue = 0;
      if (typeof a[sortKey] === 'string') {
        sortValue = a[sortKey].localeCompare(b[sortKey]);
      } else if (typeof a[sortKey] === 'number') {
        sortValue = Number(a[sortKey]) - Number(b[sortKey]);
      } else if (typeof a[sortKey] === 'boolean') {
        sortValue = a[sortKey] === b[sortKey] ? 0 : a[sortKey] ? -1 : 1;
      }

      return sortOrder === 'asc' ? sortValue : -sortValue;
    },
    [sortKey, sortOrder],
  );

  const sortedData = useMemo(() => {
    return data.slice().sort(compareRows);
  }, [data, compareRows]);

  const displayData = useMemo<DisplayFlowRow[]>(() => {
    if (!sortedData?.length) {
      return [];
    }

    const byGroup = new Map<string, FlowRow[]>();

    sortedData.forEach((row) => {
      const provider = row.key?.provider ?? 'UNKNOWN';
      const base = `component:${row.id}`;
      const groupKey = `${base}|provider:${provider}`;

      if (!byGroup.has(groupKey)) {
        byGroup.set(groupKey, []);
      }
      byGroup.get(groupKey)!.push(row);
    });

    const groupOrder = Array.from(byGroup.keys()).sort((groupA, groupB) => {
      const rowsA = byGroup.get(groupA) ?? [];
      const rowsB = byGroup.get(groupB) ?? [];

      const parentA = rowsA.find((row) => row.key?.view === 'net') ?? rowsA[0];
      const parentB = rowsB.find((row) => row.key?.view === 'net') ?? rowsB[0];

      if (!parentA || !parentB) {
        return 0;
      }

      return compareRows(parentA, parentB);
    });

    const out: DisplayFlowRow[] = [];

    groupOrder.forEach((groupKey) => {
      const rows = byGroup.get(groupKey) ?? [];
      if (!rows.length) {
        return;
      }

      const netRow = rows.find((row) => row.key?.view === 'net');
      const rawRows = rows.filter((row) => row.key?.view === 'raw');
      const parent = netRow ?? rows[0];
      const rawChildren = rawRows.filter((row) => row !== parent);
      const isExpanded = Boolean(expandedGroups[groupKey]);

      out.push({
        ...parent,
        rowKey: `${String(parent.compositeId)}|${groupKey}|parent`,
        groupKey,
        isRollupParent: true,
        isRawChild: false,
        rawChildCount: rawChildren.length,
      });

      if (isExpanded) {
        rawChildren.forEach((child) => {
          out.push({
            ...child,
            rowKey: `${String(child.compositeId)}|${groupKey}|raw:${String(
              child.key?.normalized_provider_key ?? 'none',
            )}`,
            groupKey,
            isRollupParent: false,
            isRawChild: true,
            rawChildCount: 0,
          });
        });
      }
    });

    return out;
  }, [sortedData, expandedGroups, compareRows]);

  // Dispatch flows to map plotter based on PUSH_VISIBLE_ROWS toggle
  useMemo(() => {
    if (!flows) {
      dispatch(plotterActions.setCapacities([]));
      return;
    }

    const flowIds = PUSH_VISIBLE_ROWS
      ? displayData.map((row) => row.id) // all visible rows (parents + expanded children)
      : displayData.filter((row) => row.isRollupParent).map((row) => row.id); // parent rows only

    const flowsToDispatch = Object.values(flows).filter(
      (flow) => flowIds.includes(flow.id) && !flow.is_unmatched_provider_link,
    );

    dispatch(plotterActions.setCapacities(flowsToDispatch));
  }, [displayData, flows, dispatch]);

  const paginationResetKey = useMemo(
    () => sortedData.map((row) => String(row.compositeId)).join(','),
    [sortedData],
  );

  const toggleGroupExpanded = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const {
    slice: paginatedData,
    exhausted: clientExhausted,
    getNext,
  } = useClientPagination(displayData, 30, paginationResetKey);

  const shouldFetchServerPage =
    clientExhausted && !allMappedSegments && !flowsLoading && !flowsFetching;
  const infiniteScrollHandler = !clientExhausted
    ? getNext
    : shouldFetchServerPage
    ? fetchNextPage
    : undefined;
  const showFooterLoading = flowsLoading || flowsFetching;

  const setSorting = (key: keyof FlowRow) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  if (!sortedData) {
    return null;
  }

  const headers: { title: string; sortKey?: keyof FlowRow }[] = [
    { title: 'Favorite', sortKey: 'isFavorite' },
    { title: 'Go To' },
    { title: 'History' },
    {
      title: 'Pipeline',
      sortKey: 'pipeline',
    },
    { title: 'Description' },
    { title: 'Source' },
    {
      title: 'Type',
      sortKey: 'type',
    },
    {
      title: 'Direction',
      sortKey: 'ourDirection',
    },
    { title: 'Gas Date' },
    {
      title: 'Cycle',
      sortKey: 'cycle',
    },
    {
      title: 'Scheduled',
      sortKey: 'scheduled',
    },
    {
      title: 'Utilization',
      sortKey: 'utilization',
    },
    {
      title: 'OAC',
      sortKey: 'oac',
    },
    {
      title: 'Op Cap',
      sortKey: 'opcap',
    },
    {
      title: 'IT Flow?',
      sortKey: 'itFlow',
    },
    // Commenting out for now until we decide how we want to show notices alongside flows in table again
    // {
    //   title: 'Notice Priority',
    //   sortKey: 'noticePriority',
    // },
    // {
    //   title: 'Notice OAC',
    //   sortKey: 'noticeOAC',
    // },
    // {
    //   title: 'Notice %',
    //   sortKey: 'noticePercent',
    // },
    { title: 'Locations' },
  ];

  return (
    <>
      <div
        className="row justify-content-between m-3 position-sticky"
        style={{ zIndex: 30, top: 0, background: 'var(--bs-body-bg)' }}
      >
        <div className="col-6 col-sm-8 col-xs-11">
          <div className="input-group">
            <span className="input-group-text">
              <SearchIcon />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by description"
              value={searchText}
              onChange={(evt) => {
                const nextSearch = evt.target.value;
                setSearchText(nextSearch);
                if (nextSearch && allMappedSegments) {
                  setAllMappedSegments(false);
                }
              }}
            />
            <button
              type="button"
              className={`btn ${
                allMappedSegments ? 'btn-primary' : 'btn-outline-secondary'
              }`}
              onClick={() => {
                setAllMappedSegments((prev) => {
                  const next = !prev;
                  if (next) {
                    setSearchText('');
                  }
                  return next;
                });
              }}
              title="Load all mapped segment flows"
            >
              All Segment Flows
            </button>
          </div>
        </div>
        <div className="col">
          <select
            className={`form-select ${
              capacityTypeFilter ? '' : 'text-secondary'
            }`}
            aria-label="Capacity Type"
            value={capacityTypeFilter || ''}
            onChange={(evt) =>
              setCapacityTypeFilter(
                (evt.target.value || undefined) as CapacityType | undefined,
              )
            }
          >
            <option value="">Capacity Type</option>
            {CAPACITY_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col">
          <MultiSelectSearch
            placeholder="Pipelines"
            options={tspOptions}
            onChange={(value) => {
              setFilterTsps(value.map((v) => v.value));
            }}
            value={filterTsps}
          />
        </div>
      </div>
      <div style={{ minHeight: 200 }}>
        <div>
          {displayData?.length === 0 && flowsLoading === false ? (
            <div className="alert alert-info m-3 text-center" role="alert">
              {searchText.length > 0
                ? `No results for ${searchText}, try a different search.`
                : 'No flows found. Try a different filter.'}
            </div>
          ) : (
            <>
              <div
                ref={setTableScrollRoot}
                style={{
                  height: tableScrollHeight ?? 360,
                  maxHeight: tableScrollHeight ?? 360,
                  overflowY: 'auto',
                  overflowX: 'auto',
                  overscrollBehavior: 'contain',
                }}
              >
                <table
                  className="table"
                  style={{
                    width: 'max-content',
                    minWidth: '100%',
                    position: 'relative',
                    tableLayout: 'auto',
                    marginBottom: 0,
                  }}
                >
                  <thead>
                    <tr>
                      {headers.map((header) => (
                        <th
                          key={header.title}
                          style={{
                            cursor: header.sortKey ? 'pointer' : '',
                            position: 'sticky',
                            top: 0,
                            zIndex: 20,
                            background: 'var(--bs-body-bg)',
                          }}
                          onClick={() => {
                            if (header.sortKey) {
                              setSorting(header.sortKey);
                            }
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <span>{header.title}</span>
                            {sortKey === header.sortKey ? (
                              sortOrder === 'asc' ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            ) : (
                              ''
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((row) => (
                      <tr
                        key={row.rowKey}
                        style={
                          row.isRawChild
                            ? {
                                backgroundColor: 'var(--bs-secondary-bg)',
                                color: 'var(--bs-secondary-color)',
                                boxShadow:
                                  'inset 4px 0 0 var(--bs-secondary-color)',
                                opacity: 0.78,
                              }
                            : undefined
                        }
                      >
                        <td>
                          {row.isRawChild ? (
                            <div style={{ textAlign: 'center' }}>-</div>
                          ) : (
                            <div className="d-flex justify-content-center align-items-center">
                              <button
                                style={{ width: '50px' }}
                                disabled={
                                  toggleFavoriteLoading ||
                                  row.isUnmatchedProviderLink
                                }
                                className="btn btn-light"
                                onClick={() => toggleFavorite(row.id)}
                              >
                                <StarIcon
                                  color={
                                    getFavorite(row.id) ? 'yellow' : 'unset'
                                  }
                                />
                              </button>
                            </div>
                          )}
                        </td>
                        <td>
                          {row.isRawChild ? (
                            <div style={{ textAlign: 'center' }}>-</div>
                          ) : (
                            <div className="d-flex justify-content-center align-items-center">
                              <button
                                style={{ width: '50px' }}
                                className="btn btn-light"
                                disabled={
                                  row.isUnmatchedProviderLink ||
                                  !allNodes[row.nodes[0]]
                                }
                                onClick={() => {
                                  if (row.isUnmatchedProviderLink) {
                                    return;
                                  }
                                  const nodes = row.nodes.map(
                                    (nodeId) => allNodes[nodeId],
                                  );
                                  const point = row.points.find((pointId) => {
                                    return nodes.some((node) =>
                                      node.points.includes(pointId),
                                    );
                                  });
                                  if (point) {
                                    focusPoint(point);
                                  }
                                }}
                              >
                                <ZoomInIcon />
                              </button>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="d-flex justify-content-center align-items-center">
                            <button
                              style={{ width: '50px' }}
                              className="btn btn-light"
                              disabled={!row.key || !row.gasDate}
                              onClick={() => {
                                if (!row.key || !row.gasDate) {
                                  console.error(
                                    'No flow slice key available for this row, cannot show history',
                                  );
                                  return;
                                }
                                setFlowSliceKey(row.key);
                                setComponentHistoryDescription(
                                  row.pipeline + ' - ' + row.description,
                                );
                              }}
                            >
                              <ChartIcon />
                            </button>
                          </div>
                        </td>
                        <td
                          style={{
                            whiteSpace: 'nowrap',
                            overflowX: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.pipeline}
                        </td>
                        <td
                          style={{
                            whiteSpace: 'nowrap',
                            overflowX: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            {row.isRollupParent && row.rawChildCount > 0 ? (
                              <button
                                type="button"
                                className="btn btn-link p-0 border-0 text-secondary d-inline-flex align-items-center gap-1 text-decoration-none"
                                onClick={() =>
                                  toggleGroupExpanded(row.groupKey)
                                }
                                title={
                                  expandedGroups[row.groupKey]
                                    ? 'Collapse raw rows'
                                    : 'Expand raw rows'
                                }
                              >
                                {row.rawChildCount > 1 && (
                                  <span>{row.rawChildCount}</span>
                                )}
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    transform: expandedGroups[row.groupKey]
                                      ? 'rotate(0deg)'
                                      : 'rotate(-90deg)',
                                    transition: 'transform 0.15s ease',
                                  }}
                                >
                                  <ChevronDownIcon />
                                </span>
                              </button>
                            ) : null}
                            <span
                              style={{
                                paddingLeft: row.isRawChild ? 18 : 0,
                              }}
                            >
                              {row.isRawChild && (
                                <>
                                  <CornerDownRight
                                    size={16}
                                    className="me-1 text-secondary"
                                    style={{ display: 'inline' }}
                                  />
                                </>
                              )}
                              {row.description}
                              {row.isRawChild && (
                                <span
                                  className="badge text-bg-secondary ms-2"
                                  style={{
                                    fontSize: '0.62rem',
                                    letterSpacing: '0.04em',
                                  }}
                                  title={row.ourDirection || ''}
                                >
                                  {`RAW ${toDirectionShort(
                                    row.ourDirection,
                                  )}`.trim()}
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td>{row.key?.provider || row.providerName || '-'}</td>
                        <td>{row.type}</td>
                        <td>{row.ourDirection ?? 'FORWARDHAUL'}</td>
                        <td>
                          {row.gasDate
                            ? new Date(row.gasDate).toLocaleDateString(
                                undefined,
                                {
                                  timeZone: 'UTC',
                                },
                              )
                            : '-'}
                        </td>
                        <td>{row.cycle || '-'}</td>
                        <td>
                          {row.scheduled != null ? (
                            numberFormatter(row.scheduled)
                          ) : (
                            <div style={{ textAlign: 'center' }}>-</div>
                          )}
                        </td>
                        <td>
                          {row.utilization != null ? (
                            `${Math.floor(row.utilization * 100)}%`
                          ) : (
                            <div style={{ textAlign: 'center' }}>-</div>
                          )}
                        </td>
                        <td>
                          {row.oac != null ? (
                            numberFormatter(row.oac)
                          ) : (
                            <div style={{ textAlign: 'center' }}>-</div>
                          )}
                        </td>
                        <td>
                          {row.opcap != null ? (
                            numberFormatter(row.opcap)
                          ) : (
                            <div style={{ textAlign: 'center' }}>-</div>
                          )}
                        </td>
                        <td>{row.itFlow}</td>
                        {/* <td>
                        {row.noticePriority ? (
                          <>
                            <div id={`tooltip-priority-${row.id}`}>
                              {row.noticePriority}
                            </div>
                            <NoticeToolTip
                              notice={row.notice}
                              tooltipId={`tooltip-priority-${row.id}`}
                            />
                          </>
                        ) : (
                          <div style={{ textAlign: 'center' }}>-</div>
                        )}
                      </td>
                      <td>
                        {row.noticeOAC ? (
                          <>
                            <div id={`tooltip-amount-${row.id}`}>
                              {row.noticeOAC !== null
                                ? row.noticeOAC.toLocaleString('en-US')
                                : ''}
                            </div>
                            <NoticeToolTip
                              notice={row.notice}
                              tooltipId={`tooltip-amount-${row.id}`}
                            />
                          </>
                        ) : (
                          <div style={{ textAlign: 'center' }}>-</div>
                        )}
                      </td>
                      <td>
                        {row.noticePercent ? (
                          <>
                            <div id={`tooltip-percent-${row.id}`}>
                              {row.noticePercent * 100}%
                            </div>
                            <NoticeToolTip
                              notice={row.notice}
                              tooltipId={`tooltip-percent-${row.id}`}
                            />
                          </>
                        ) : (
                          <div style={{ textAlign: 'center' }}>-</div>
                        )}
                      </td> */}
                        <td>
                          {row.isRawChild ? (
                            <div style={{ textAlign: 'center' }}>-</div>
                          ) : (
                            <button
                              className="btn btn-primary w-100 px-2"
                              onClick={() =>
                                setComponentId(row.compositeId as number)
                              }
                            >
                              {row.locations}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {displayData?.length > 0 && (
                  <div className="px-2 py-1">
                    {showFooterLoading ? (
                      <LoadingIndicator
                        className="w-100"
                        style={{ minHeight: 24 }}
                      />
                    ) : infiniteScrollHandler ? (
                      <InfiniteScrollObserver
                        onScrollIn={infiniteScrollHandler}
                        options={{
                          root: tableScrollRoot,
                          rootMargin: '150px 0px',
                        }}
                        height={12}
                        style={{ marginTop: 6, marginBottom: 6 }}
                      />
                    ) : null}
                  </div>
                )}
              </div>
              <ReusableModal
                onClose={() => setComponentId(false)}
                isOpen={Boolean(componentId)}
                header={component?.label}
              >
                {componentId && (
                  <ComponentLocationManager
                    componentId={componentId}
                    setComponent={setComponent}
                  />
                )}
              </ReusableModal>
              <ReusableModal
                size="xl"
                onClose={() => {
                  setFlowSliceKey(null);
                  setComponentHistoryDescription('');
                }}
                isOpen={Boolean(flowSliceKey)}
                header={componentHistoryDescription}
                scrollable
              >
                <CapacityComponentHistory flowSliceKey={flowSliceKey} />
              </ReusableModal>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// type NoticeToolTipProps = {
//   notice: Notice;
//   tooltipId: string;
// };

// const NoticeToolTip: React.FC<NoticeToolTipProps> = ({ notice, tooltipId }) => {
//   const tooltipData: { key: keyof Notice; header: string }[] = [
//     { key: 'identifier', header: 'id' },
//     { key: 'identifier_type', header: 'id type' },
//     { key: 'notice_id', header: 'notice id' },
//     { key: 'effective_datetime', header: 'date' },
//     { key: 'priority', header: 'priority' },
//     { key: 'amount', header: 'amount' },
//     { key: 'percent', header: 'percent' },
//     { key: 'reason', header: 'reason' },
//     { key: 'tsp_name', header: 'tsp' },
//     { key: 'nomination_cycle', header: 'nomination cycle' },
//   ];

//   return (
//     <UncontrolledTooltip
//       style={{ maxWidth: '80%', left: '10%', position: 'relative' }}
//       target={tooltipId}
//     >
//       <table>
//         <tbody>
//           <tr>
//             {tooltipData.map((item) => (
//               <th
//                 key={item.key}
//                 style={{
//                   border: '1px solid white',
//                   fontSize: 'bold',
//                 }}
//               >
//                 {item.header}
//               </th>
//             ))}
//           </tr>
//           <tr>
//             {tooltipData.map((item) => (
//               <td key={item.key} style={{ border: '1px solid white' }}>
//                 {item.header === 'amount'
//                   ? notice[item.key] !== null
//                     ? notice[item.key].toLocaleString('en-US')
//                     : ''
//                   : item.header === 'percent'
//                   ? `${Number(notice[item.key]) * 100}%`
//                   : notice[item.key]}
//               </td>
//             ))}
//           </tr>
//         </tbody>
//       </table>
//     </UncontrolledTooltip>
//   );
// };
